import { useState } from 'react';
import { taxApi } from '../../api/services';
import type { PurchasesBookRowDto, SalesBookRowDto, TaxRateDto, TaxSummaryLineDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Check, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Stat, Table, Tabs, firstOfMonthIso, fmtDate, fmtMoney, fmtNumber, money, todayIso, useAction, useAsync,
} from '../../ui/components';
import { PageHelp } from '../../ui/help';
import { useCountry } from '../../ui/company';
import { PERM, useCan } from '../../ui/access';

type TabKey = 'summary' | 'sales' | 'purchases' | 'rates';

const pct = (v?: number | null) => (v === null || v === undefined ? 'Sin desglose' : `${fmtNumber(v, 4)} %`);

/** Impuesto de ventas (IVA / ITBMS / ISV): resumen para declarar, libros de ventas y compras, tarifas. */
export function TaxPage() {
  const country = useCountry();
  const taxName = country?.salesTax ?? 'impuesto de ventas';
  const [tab, setTab] = useState<TabKey>('summary');
  const [range, setRange] = useState({ from: firstOfMonthIso(), to: todayIso() });
  const [draft, setDraft] = useState(range);
  const status = useAsync(() => taxApi.status(), []);
  const { run, busy } = useAction();
  const report = tab === 'summary' ? 'summary' : tab === 'sales' ? 'sales-book' : 'purchases-book';

  return (
    <>
      <PageHeader title={`Impuestos (${taxName})`} subtitle="Libros de ventas y compras y resumen para preparar la declaración"
        actions={tab !== 'rates' ? <Button busy={busy} onClick={() => run(() => taxApi.exportCsv(report, range.from, range.to))}>Exportar a Excel (CSV)</Button> : undefined} />
      <PageHelp page="tax" />
      <Tabs<TabKey> tabs={[{ key: 'summary', label: 'Resumen para declarar' }, { key: 'sales', label: 'Libro de ventas' }, { key: 'purchases', label: 'Libro de compras' }, { key: 'rates', label: 'Tarifas' }]} active={tab} onChange={setTab} />
      {tab !== 'rates' && (
        <div className="acc-toolbar">
          <Field label="Desde"><Input type="date" value={draft.from} onChange={(v) => setDraft({ ...draft, from: v })} /></Field>
          <Field label="Hasta"><Input type="date" value={draft.to} onChange={(v) => setDraft({ ...draft, to: v })} /></Field>
          <Button kind="primary" onClick={() => setRange(draft)}>Consultar</Button>
        </div>
      )}
      {tab === 'summary' && <SummaryTab from={range.from} to={range.to} />}
      {tab === 'sales' && <SalesTab from={range.from} to={range.to} />}
      {tab === 'purchases' && <PurchasesTab from={range.from} to={range.to} />}
      {tab === 'rates' && <RatesTab available={status.data?.available ?? true} />}
    </>
  );
}

function Notes({ notes }: { notes: string[] }) {
  return <>{notes.map((n) => <Alert key={n} kind="info">{n}</Alert>)}</>;
}

function SummaryTab({ from, to }: { from: string; to: string }) {
  const summary = useAsync(() => taxApi.summary(from, to), [from, to]);
  return (
    <Loadable state={summary}>
      {(s) => (
        <>
          {s.declaration.declaration && (
            <Card title="Declaración">
              <dl className="acc-dl">
                <dt>Formulario</dt><dd>{s.declaration.declaration}</dd>
                <dt>Plazo</dt><dd>{s.declaration.deadline}</dd>
                <dt>Fuente</dt><dd>{s.declaration.source} {s.declaration.declarationReview && <Badge tone="warn">Revisar</Badge>}</dd>
              </dl>
              {s.declaration.notIncluded.length > 0 && (
                <Alert kind="warn">Este resumen no incluye: {s.declaration.notIncluded.join('; ')}. Agréguelos al preparar la declaración.</Alert>
              )}
            </Card>
          )}
          <div className="acc-stats">
            <Stat label="Impuesto de ventas (débito)" value={fmtMoney(s.totalSalesTax)} />
            <Stat label="Impuesto de compras (crédito)" value={fmtMoney(s.totalPurchasesTax)} />
            <Stat label={s.net >= 0 ? 'Débito menos crédito (a pagar)' : 'Crédito mayor que débito (a favor)'} value={fmtMoney(Math.abs(s.net))} tone={s.net >= 0 ? 'neg' : 'pos'} />
          </div>
          <Table<TaxSummaryLineDto> rows={s.lines} rowKey={(l) => `${l.label}|${l.percent ?? ''}`} columns={[
            { key: 'l', title: 'Tarifa', render: (l) => l.label },
            { key: 'b', title: 'Renglón de la declaración', render: (l) => <span className="acc-muted">{l.box ?? ''}</span> },
            money<TaxSummaryLineDto>('sb', 'Ventas (base)', (l) => l.salesBase),
            money<TaxSummaryLineDto>('st', 'Impuesto ventas', (l) => l.salesTax),
            money<TaxSummaryLineDto>('pb', 'Compras (base)', (l) => l.purchasesBase),
            money<TaxSummaryLineDto>('pt', 'Impuesto compras', (l) => l.purchasesTax),
          ]} />
          <p className="acc-muted">Montos en {s.currency}, del {fmtDate(s.dateFrom)} al {fmtDate(s.dateTo)}.</p>
          <Notes notes={s.notes} />
        </>
      )}
    </Loadable>
  );
}

function SalesTab({ from, to }: { from: string; to: string }) {
  const book = useAsync(() => taxApi.salesBook(from, to), [from, to]);
  return (
    <Loadable state={book}>
      {(b) => (
        <>
          <Table<SalesBookRowDto> rows={b.rows} rowKey={(r) => `${r.documentId}|${r.percent ?? ''}`} empty="No hay documentos en el rango." columns={[
            { key: 'f', title: 'Fecha', render: (r) => fmtDate(r.date) },
            { key: 't', title: 'Tipo', render: (r) => r.type },
            { key: 'n', title: 'Número', render: (r) => r.number },
            { key: 'c', title: 'Cliente', render: (r) => <>{r.customerName}{r.customerId && <div className="acc-muted">{r.customerId}</div>}</> },
            money<SalesBookRowDto>('e', 'Exento', (r) => r.exempt || null),
            { key: 'p', title: 'Tarifa', render: (r) => (r.source === 'Exento' ? '' : r.source === 'Sin desglose' ? <Badge tone="warn">Sin desglose</Badge> : pct(r.percent)) },
            money<SalesBookRowDto>('g', 'Gravado', (r) => r.taxable || null),
            money<SalesBookRowDto>('i', 'Impuesto', (r) => r.tax || null),
            money<SalesBookRowDto>('to', 'Total', (r) => r.total || null),
          ]} footer={
            <tr>
              <td colSpan={4}><strong>Totales ({b.rows.length} líneas)</strong></td>
              <td style={{ textAlign: 'right' }}><strong>{fmtMoney(b.rows.reduce((a, r) => a + r.exempt, 0))}</strong></td>
              <td />
              <td style={{ textAlign: 'right' }}><strong>{fmtMoney(b.rows.reduce((a, r) => a + r.taxable, 0))}</strong></td>
              <td style={{ textAlign: 'right' }}><strong>{fmtMoney(b.rows.reduce((a, r) => a + r.tax, 0))}</strong></td>
              <td style={{ textAlign: 'right' }}><strong>{fmtMoney(b.rows.reduce((a, r) => a + r.total, 0))}</strong></td>
            </tr>
          } />
          <Notes notes={b.notes} />
        </>
      )}
    </Loadable>
  );
}

function PurchasesTab({ from, to }: { from: string; to: string }) {
  const book = useAsync(() => taxApi.purchasesBook(from, to), [from, to]);
  return (
    <Loadable state={book}>
      {(b) => (
        <>
          <Table<PurchasesBookRowDto> rows={b.rows} rowKey={(r) => r.entryCode} empty="No hay facturas de proveedores en el rango." columns={[
            { key: 'f', title: 'Fecha', render: (r) => fmtDate(r.date) },
            { key: 's', title: 'Proveedor', render: (r) => <>{r.supplierName}{r.supplierId && <div className="acc-muted">{r.supplierId}</div>}</> },
            { key: 'n', title: 'Factura', render: (r) => r.invoiceNumber },
            money<PurchasesBookRowDto>('e', 'Exento', (r) => r.exempt || null),
            { key: 'p', title: 'Tarifa', render: (r) => pct(r.percent) },
            money<PurchasesBookRowDto>('g', 'Gravado', (r) => r.taxable || null),
            money<PurchasesBookRowDto>('i', 'Impuesto', (r) => r.tax || null),
            money<PurchasesBookRowDto>('to', 'Total', (r) => r.total),
            { key: 'x', title: 'Gasto', render: (r) => (r.isExpense ? 'Sí' : '') },
          ]} footer={
            <tr>
              <td colSpan={3}><strong>Totales ({b.rows.length})</strong></td>
              <td style={{ textAlign: 'right' }}><strong>{fmtMoney(b.rows.reduce((a, r) => a + r.exempt, 0))}</strong></td>
              <td />
              <td style={{ textAlign: 'right' }}><strong>{fmtMoney(b.rows.reduce((a, r) => a + r.taxable, 0))}</strong></td>
              <td style={{ textAlign: 'right' }}><strong>{fmtMoney(b.rows.reduce((a, r) => a + r.tax, 0))}</strong></td>
              <td style={{ textAlign: 'right' }}><strong>{fmtMoney(b.rows.reduce((a, r) => a + r.total, 0))}</strong></td>
              <td />
            </tr>
          } />
          <Notes notes={b.notes} />
        </>
      )}
    </Loadable>
  );
}

function RatesTab({ available }: { available: boolean }) {
  const rates = useAsync(() => taxApi.rates(), []);
  const canEdit = useCan(PERM.setup);
  const [editing, setEditing] = useState<TaxRateDto | 'new' | null>(null);
  const { run, busy } = useAction();
  if (!available) return <Alert kind="warn">Esta función todavía no está habilitada en la base de datos de su empresa. Contacte al administrador del sistema.</Alert>;
  return (
    <>
      {canEdit && (
        <div className="acc-actions" style={{ justifyContent: 'flex-start' }}>
          <Button kind="primary" onClick={() => setEditing('new')}>Nueva tarifa</Button>
          <Button busy={busy} onClick={() => run(async () => { await taxApi.loadCountryDefaults(); rates.reload(); }, { success: 'Tarifas del país cargadas.' })}>Cargar tarifas del país</Button>
          <span className="acc-muted">Sólo agrega las que falten; no cambia las que usted modificó.</span>
        </div>
      )}
      <Loadable state={rates}>
        {(r) => (
          <Table<TaxRateDto> rows={r} rowKey={(x) => x.id} empty='No hay tarifas. Pulse "Cargar tarifas del país".' columns={[
            { key: 'n', title: 'Tarifa', render: (x) => <><strong>{x.name}</strong>{x.note && <div className="acc-muted">{x.note}</div>}</> },
            { key: 'p', title: '%', align: 'right', render: (x) => pct(x.percent) },
            { key: 'b', title: 'Renglón de la declaración', render: (x) => x.box ?? '' },
            { key: 's', title: 'Estado', render: (x) => <>{x.active ? <Badge tone="ok">Activa</Badge> : <Badge>Inactiva</Badge>} {x.review && <Badge tone="warn">Revisar</Badge>}</> },
            { key: 'x', title: '', render: (x) => (canEdit ? <Button kind="link" onClick={() => setEditing(x)}>Editar…</Button> : null) },
          ]} />
        )}
      </Loadable>
      {editing && <RateModal rate={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); rates.reload(); }} />}
    </>
  );
}

function RateModal({ rate, onClose, onSaved }: { rate: TaxRateDto | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ code: rate?.code ?? '', name: rate?.name ?? '', percent: rate?.percent ?? 0, box: rate?.box ?? '', active: rate?.active ?? true, note: rate?.note ?? '' });
  const { run, busy, fieldErrors } = useAction();
  return (
    <Modal title={rate ? `Editar ${rate.name}` : 'Nueva tarifa'} onClose={onClose} size="sm">
      <Form onSubmit={() => run(async () => { await taxApi.saveRate({ ...form, box: form.box || null, note: form.note || null }); onSaved(); }, { success: 'Tarifa guardada.' })}>
        <Field label="Código" hint="Letras y números, sin espacios" error={fieldErrors.Code}><Input value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} maxLength={20} required disabled={!!rate} /></Field>
        <Field label="Nombre" error={fieldErrors.Name}><Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} maxLength={100} required /></Field>
        <Field label="Porcentaje" error={fieldErrors.Percent}><NumberInput step="0.0001" min={0} max={100} value={form.percent} onChange={(v) => setForm({ ...form, percent: v })} /></Field>
        <Field label="Renglón de la declaración" hint="Texto de ayuda: dónde se reporta"><Input value={form.box} onChange={(v) => setForm({ ...form, box: v })} maxLength={200} /></Field>
        <Field label="Nota"><Input value={form.note} onChange={(v) => setForm({ ...form, note: v })} maxLength={400} /></Field>
        <Check checked={form.active} onChange={(v) => setForm({ ...form, active: v })} label="Activa" />
        <div className="acc-actions"><Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button></div>
      </Form>
    </Modal>
  );
}
